import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { startOfDay } from "date-fns";
import type { StoreId, StaffId } from "@KassaroAB/shared/types/ids";
import { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import type { ReceiptFilters } from "../../../api/receiptsApi";
import { useGetStaffQuery } from "../../../query/staffQuery";
import { Button } from "../../../components/Button";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { SelectInput } from "../../../components/SelectInput";
import {
  buildReceiptFilters,
  datePresets,
  formatDateForInput,
  getPresetDate,
  getPresetLabel,
  initFromFilters,
  parseDateInput,
  todayMaxDate,
} from "../../../utils/receiptFilterUtils";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  background-color: ${colors.background};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  align-items: flex-end;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  min-width: 0;
  flex: 1 1 140px;
`;

const PresetsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
`;

const PresetButton = styled.button<{ $active: boolean }>`
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.regular}px;
  border: 1px solid
    ${({ $active }) => ($active ? colors.primary : colors.border)};
  background-color: ${({ $active }) =>
    $active ? colors.primary : colors.background};
  color: ${({ $active }) =>
    $active ? colors.primaryContrastText : colors.text};
  font-size: ${fontSizes.small}px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
  }
`;

const DateInput = styled.input`
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  background-color: ${colors.background};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

const TimeInput = styled(DateInput)`
  min-width: 100px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${spacing.x1}px;
`;

interface ReceiptsFilterBarProps {
  storeId: StoreId;
  businessDayStartHour: number;
  filters?: ReceiptFilters;
  onFiltersChange: (filters: ReceiptFilters | undefined) => void;
  isExpanded: boolean;
}

export const ReceiptsFilterBar = ({
  storeId,
  businessDayStartHour,
  filters,
  onFiltersChange,
  isExpanded,
}: ReceiptsFilterBarProps) => {
  const { t } = useTranslation();
  const { data: staffMembers } = useGetStaffQuery(storeId);
  const initial = initFromFilters(
    businessDayStartHour,
    filters?.startDate,
    filters?.endDate,
  );
  const [selectedType, setSelectedType] = useState<ReceiptType | undefined>(
    filters?.type,
  );
  const [selectedStaffId, setSelectedStaffId] = useState<StaffId | undefined>(
    filters?.staffId,
  );
  const [selectedPreset, setSelectedPreset] = useState<string | undefined>();
  const [date, setDate] = useState<Date | undefined>(initial.date);
  const [startTime, setStartTime] = useState<string | undefined>(
    initial.startTime,
  );
  const [endTime, setEndTime] = useState<string | undefined>(initial.endTime);

  const buildFilters = useCallback((): ReceiptFilters | undefined => {
    return buildReceiptFilters({
      selectedType,
      selectedStaffId,
      date,
      startTime,
      endTime,
      businessDayStartHour,
    });
  }, [
    selectedType,
    selectedStaffId,
    date,
    startTime,
    endTime,
    businessDayStartHour,
  ]);

  useEffect(() => {
    onFiltersChange(buildFilters());
  }, [buildFilters, onFiltersChange]);

  const handleClear = () => {
    setSelectedType(undefined);
    setSelectedStaffId(undefined);
    setSelectedPreset(undefined);
    setDate(undefined);
    setStartTime(undefined);
    setEndTime(undefined);
  };

  const getTypeLabel = (type: ReceiptType): string => {
    return t(`statistics.receiptType.${type}`);
  };

  if (!isExpanded) {
    return null;
  }

  return (
    <FilterPanel>
      <FilterRow>
        <FilterField>
          <FormLabel>{t("receiptsView.filter.type")}</FormLabel>
          <SelectInput
            value={selectedType ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedType(value ? (value as ReceiptType) : undefined);
            }}
          >
            <option value="">{t("receiptsView.filter.all")}</option>
            {Object.values(ReceiptType).map((type) => (
              <option key={type} value={type}>
                {getTypeLabel(type)}
              </option>
            ))}
          </SelectInput>
        </FilterField>
        <FilterField>
          <FormLabel>{t("receiptsView.filter.staff")}</FormLabel>
          <SelectInput
            value={selectedStaffId ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedStaffId(value ? (value as StaffId) : undefined);
            }}
          >
            <option value="">{t("receiptsView.filter.all")}</option>
            {(staffMembers ?? []).map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </SelectInput>
        </FilterField>
      </FilterRow>
      <FormGroup>
        <FormLabel>{t("receiptsView.filter.dateRange")}</FormLabel>
        <PresetsRow>
          {datePresets.map((preset) => (
            <PresetButton
              key={preset.id}
              type="button"
              $active={selectedPreset === preset.id}
              onClick={() => {
                setSelectedPreset(preset.id);
                setDate(getPresetDate(preset.daysAgo, businessDayStartHour));
                setStartTime(undefined);
                setEndTime(undefined);
              }}
            >
              {getPresetLabel(preset, t, businessDayStartHour)}
            </PresetButton>
          ))}
        </PresetsRow>
      </FormGroup>
      <FilterRow>
        <FilterField>
          <FormLabel>{t("receiptsView.filter.date")}</FormLabel>
          <DateInput
            type="date"
            value={formatDateForInput(date)}
            max={todayMaxDate()}
            onChange={(e) => {
              const parsed = parseDateInput(e.target.value);
              if (parsed) {
                setDate(startOfDay(parsed));
                setSelectedPreset(undefined);
              } else if (!e.target.value) {
                setDate(undefined);
                setSelectedPreset(undefined);
              }
            }}
          />
        </FilterField>
        <FilterField>
          <FormLabel>{t("receiptsView.filter.startTime")}</FormLabel>
          <TimeInput
            type="time"
            value={startTime ?? ""}
            onChange={(e) => {
              setStartTime(e.target.value || undefined);
              setSelectedPreset(undefined);
            }}
          />
        </FilterField>
        <FilterField>
          <FormLabel>{t("receiptsView.filter.endTime")}</FormLabel>
          <TimeInput
            type="time"
            value={endTime ?? ""}
            onChange={(e) => {
              setEndTime(e.target.value || undefined);
              setSelectedPreset(undefined);
            }}
          />
        </FilterField>
        <ActionsRow>
          <Button type="button" $color="secondary" $small onClick={handleClear}>
            {t("receiptsView.filter.clear")}
          </Button>
        </ActionsRow>
      </FilterRow>
    </FilterPanel>
  );
};
